import { useEffect, useState, useMemo, useRef } from "react";
import { withTranslation } from "react-i18next";
import { AgGridReact } from "ag-grid-react";
import { includes, isEmpty, isEqual, isNull, map, sortBy, xor, cloneDeep } from "lodash";

import { Box, Button, IconButton, Menu, MenuItem, Popover, Typography } from "@mui/material";
import ResetIcon from "@mui/icons-material/RotateLeft";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import BaseConfigModal from "@plannel/components/modal/BaseConfigModal";
import { AddButton, RemoveButton, SaveButton } from "@plannel/components/ActionIconButton";
import DataState from "@plannel/components/aggrid/DataState";
import CustomHeaderMenuIcon from "@plannel/components/aggrid/CustomHeaderMenuIcon";

import CodeUtil from "@plannel/pages/utils/code-util";
import p13nService from "@plannel/services/system/p13n-service";
import DefaultGridSetting from "@plannel/components/aggrid/DefaultGridSetting";
import GridUtils from "@plannel/components/aggrid/GridUtils";
import ColorUtil from "@plannel/pages/utils/color-util";
import Dialog from "@plannel/components/Dialog"
import Snackbar from "@plannel/components/Snackbar";
import debug from "@plannel/utils/debug-util";
import { FS } from "@plannel/utils/font-util";

const MODAL_TITLE = "measureSetting";
const DIALOG_TITLE = { SAVE: "SAVE", RESET: "RESET", CLOSE: "CLOSE", DELETE: "DELETE" };
const { snackBarMessageColor } = ColorUtil;
const MEASURE = CodeUtil.ipRpMpMeasure;
const calculationOption = ["sum", "avg"];

/**
 * 빈 name 검증
 */
const validateEmptyNames = (measures, t) => {
  const emptyNames = measures.filter(item => !item.name || item.name.trim() === "");
  
  if (!isEmpty(emptyNames)) {
    return {
      isValid: false,
      message: `${t("measure")} ${t("MSG_InputMandatory")}`
    };
  }
  
  return { isValid: true };
};

/**
 * name 중복 검증 (개인 + 공유)
 */
const validateDuplicateNames = (gridRef, t) => {
    // 2. name 중복 체크 (개인 + 공유)
    const nameMap = new Map();
    const duplicates = new Set();

    gridRef.current?.api?.forEachNode(node => {
      const { name } = node.data;
      const trimmedName = name.trim();
      
      if (nameMap.has(trimmedName)) {
        duplicates.add(trimmedName);
      } else {
        nameMap.set(trimmedName, true);
      }
    });

  if (duplicates.size > 0) {
    return {
      isValid: false,
      message: `${t("MSG_DuplicateData")}: ${Array.from(duplicates).join(", ")}`
    };
  }
  
  return { isValid: true };
};

/**
 * aggregationPeriod 검증
 */
const validateAggregationPeriod = (measures, t) => {
  const invalidRangePeriods = measures.filter(item => 
    item.useRangeAggregation && (isNull(item.aggregationPeriod) || item.aggregationPeriod === 0)
  );
  
  if (!isEmpty(invalidRangePeriods)) {
    const invalidNames = invalidRangePeriods.map(item => item.name).join(", ");
    return {
      isValid: false,
      message: `[${t("period")}] ${t("MSG_InvalidValue")}: ${invalidNames}`
    };
  }
  
  return { isValid: true };
};

/**
 * 참조 유효성 검증 (존재하지 않는 참조 + 순환 참조 통합)
 * 
 * 이 함수는 두 가지 검증을 순차적으로 수행합니다:
 * 1. 존재하지 않는 measure 참조 체크
 * 2. 순환 참조 체크 (DFS 알고리즘)
 * 
 * @param {Array} allComputeMeasures - 모든 개인 measure
 * @param {Array} allSharedComputeMeasures - 모든 공유 measure
 * @param {Array} measureData - 기본 measure 데이터
 * @param {Object} measureKeyMap - measure 키 매핑
 */
const validateReferences = (allComputeMeasures, allSharedComputeMeasures, measureData, measureKeyMap, t) => {
  const allMeasuresForValidation = [
    ...allComputeMeasures,
    ...allSharedComputeMeasures
  ];

  const currentMeasureNames = new Set(allMeasuresForValidation.map(cm => cm.name));

  const basicMeasureNames = new Set();
  
  measureData.forEach(m => {
    const measureKey = measureKeyMap[m.measure] || m.measure;

    basicMeasureNames.add(measureKey);
  });

  // 존재하지 않는 measure 참조 체크
  const invalidReferences = [];

  allMeasuresForValidation.forEach(cm => {
    const referencedMeasures = (cm.formulaExp || "").match(/\[([^\]]+)\]/g);
    
    if (referencedMeasures) {
      referencedMeasures.forEach(ref => {
        const refName = ref.slice(1, -1);
        
        const isValidRef = currentMeasureNames.has(refName) || basicMeasureNames.has(refName);

        if (!isValidRef) {
          invalidReferences.push({
            measure: cm.name,
            invalidRef: refName
          });
        }
      });
    }
  });

  if (!isEmpty(invalidReferences)) {
    const errorMsg = invalidReferences.map(ref => 
      `"${ref.measure}" → [${ref.invalidRef}]`
    ).join(', ');
    
    return {
      isValid: false,
      message: `${t("MSG_InvalidFormulaReference")}: ${errorMsg}`
    };
  }

  // 2) 순환 참조 체크 (DFS)
  const dependencyGraph = new Map();
  
  allMeasuresForValidation.forEach(cm => {
    const referencedMeasures = (cm.formulaExp || "").match(/\[([^\]]+)\]/g);
    const dependencies = referencedMeasures 
      ? referencedMeasures.map(ref => ref.slice(1, -1))
      : [];
    dependencyGraph.set(cm.name, dependencies);
  });

  const detectCycle = (measureName, visited = new Set(), path = []) => {
    if (path.includes(measureName)) {
      const cycleStart = path.indexOf(measureName);
      const cycle = [...path.slice(cycleStart), measureName];
      return cycle;
    }

    if (visited.has(measureName)) {
      return null;
    }

    visited.add(measureName);
    const dependencies = dependencyGraph.get(measureName) || [];

    for (const dep of dependencies) {
      if (dependencyGraph.has(dep)) {
        const cycle = detectCycle(dep, visited, [...path, measureName]);
        if (cycle) {
          return cycle;
        }
      }
    }

    return null;
  };

  for (const cm of allMeasuresForValidation) {
    const cycle = detectCycle(cm.name);
    if (cycle) {
      const cycleStr = cycle.join(' → ');
      return {
        isValid: false,
        message: `${t("MSG_InvalidFormulaReference")}: ${cycleStr}`
      };
    }
  }

  return { isValid: true };
};

/**
 * 이름 변경 처리 (formulaExp 내 참조 업데이트)
 * @param {Object} nameChanges - oldName → newName 매핑
 * @param {Array} allComputeMeasures - 모든 개인 measure
 * @param {Array} allSharedComputeMeasures - 모든 공유 measure
 */
const processNameChanges = (nameChanges, allComputeMeasures, allSharedComputeMeasures, computeMeasureNameToCdMap, translationMap, computeMeasures) => {
   if (isEmpty(nameChanges)) {
    return;
  }

  const updateMeasure = (measure) => {
    let updatedFormulaExp = measure.formulaExp;
    
    // formulaExp 업데이트 
    Object.entries(nameChanges).forEach(([oldName, newName]) => {
      const regex = new RegExp(`\\[${oldName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\]`, 'g');
      updatedFormulaExp = updatedFormulaExp.replace(regex, `[${newName}]`);
    });
    
    if (updatedFormulaExp === measure.formulaExp) {
      return; // 변경 없음
    }

    // formulaExp 업데이트
    measure.formulaExp = updatedFormulaExp;

    // formulaExpValue 재계산 
    const originalFormulaValue = (updatedFormulaExp || "").replace(
      /\[([^\]]+)\]/g,
      (match, translatedName) => {
        const originalName = translationMap[translatedName] || translatedName;
        return `[${originalName}]`;
      }
    );
    
    const convertedFormulaExpValue = originalFormulaValue.replace(
      /\[([^\]]+)\]/g,
      (match, measureName) => {
        return computeMeasures.indexOf(measureName) < 0 
          ? `[${toUpperSnakeCase(measureName)}]` 
          : `[${computeMeasureNameToCdMap[measureName]}]`;
      }
    );

    measure.formulaExpValue = convertedFormulaExpValue; 
  };

  allComputeMeasures.forEach(measure => {
    updateMeasure(measure);
  });

  allSharedComputeMeasures.forEach(measure => {
    updateMeasure(measure);
  });
};

const getSafeBaseCd = (userData, name) => {
  const username = userData.username || "user";
  const beforeAt = username.split('@')[0];
  
  // Keep only letters, numbers, and underscores
  const safeUsername = beforeAt.replace(/[^\p{L}\p{N}_]/gu, '');
  const safeName = (name || '').replace(/[^\p{L}\p{N}_]/gu, '');
  
  return safeName ? `${safeUsername}_${safeName}` : safeUsername; 
};

// Converts camelCase to UPPER_SNAKE_CASE
const toUpperSnakeCase = (str) => {
  return str
    .replace(/([A-Z])/g, '_$1')  // 대문자 앞에 _ 추가
    .toUpperCase()                
    .replace(/^_/, '');           // 맨 앞의 _ 제거
};
  
const MeasureModal = (props) => {
  const { t, open, setOpen, viewName, allMeasure, requiredMeasure = [], applyMeasures, displayNames, measureType = null, measureKeyMap = {}, defaultMeasure, availableMeasures = [], enableCustomMeasure = false, hasDisplayName = false } = props;
  const [gridApi, setGridApi] = useState(null);
  const rightGridRef = useRef();
  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });
  const [dialogInfo, setDialogInfo] = useState({ open: false, title: "", content: "" });
  const [measureData, setMeasureData] = useState([]);
  const [selectedMeasure, setSelectedMeasure] = useState([]);
  const [isExpanded, setIsExpanded] = useState(false);
  const [gridLoading, setGridLoading] = useState(false);
  const [popoverEl, setPopoverEl] = useState(null);
  const [menuEl, setMenuEl] = useState(null);
  const menuOpen = Boolean(menuEl);
  const editingNodeRef = useRef(null);
  const [computeMeasureData, setComputeMeasureData] = useState([]);
  const [originComputeMeasureData, setOriginComputeMeasureData] = useState([]);
  const userData = JSON.parse(localStorage.getItem("user"));
  const [anchorEl, setAnchorEl] = useState(null);
  const addListOpen = Boolean(anchorEl);
  const common = "_COMMON";

  const measureTranslationMap = useMemo(() => {
    const map = {};
    
    measureData.forEach(item => {
      const measureKey = measureKeyMap[item.measure] || item.measure;
      const translatedMeasure = GridUtils.gridValueL10N(measureKey);

      map[translatedMeasure] = measureKey;
    });

    computeMeasureData?.forEach(cm => {
        map[cm.name] = cm.name; 
    });

    return map;

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [measureData, measureKeyMap]);

  const popoverClose = () => {
    setPopoverEl(null);
  };

  const popoverOpen = Boolean(popoverEl);
  const popoverId = popoverOpen ? "simple-popover" : null;

  const stableAvailableMeasures = useMemo(() => availableMeasures
    // eslint-disable-next-line react-hooks/exhaustive-deps
  , [JSON.stringify(availableMeasures)]);
  useEffect(() => {
    if (!isEmpty(selectedMeasure)) {
      setGridData(selectedMeasure, displayNames);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stableAvailableMeasures, computeMeasureData, displayNames]);

  const dynamicCellStyle = params => {
    if (!isEmpty(availableMeasures)) {
      const isAvailable = includes(availableMeasures, params.data.measure);
      if (!isAvailable) {
        return {
          backgroundColor: '#f5f5f5',
          color: '#9e9e9e',
          opacity: 0.6,
          cursor: 'not-allowed'
        };
      }
    }
    return {};
  };

  const dynamicEditable = params => {
    if (!isEmpty(availableMeasures)) {
      return includes(availableMeasures, params.data.measure);
    }

    return true;
  };

  const columnDefs = useMemo(() => {
    const baseDefs = [
      {
        headerName: "measure", field: "measure", type: ["nonEditableColumn"], flex: 1,
        valueFormatter: params => {
          if (isEmpty(measureKeyMap)) {
            return t(params.value);
          } else {
            const translationKey = measureKeyMap[params.value] || params.value;
            return Object.values(MEASURE).indexOf(translationKey) !== -1 ? t(translationKey) : t(params.value);
          }
        },
        rowDrag: params => dynamicEditable(params),
        cellStyle: dynamicCellStyle
      }
    ];
    if (hasDisplayName) {
      const displayColumnDef = {
        headerName: "displayName",
        field: "displayName",
        type: ["nonEditableColumn"],
        flex: 1,
        editable: false,  // ← 편집 가능하도록 설정
        valueFormatter: params => (params.value == null ? "" : params.value),
        // rowDrag: params => dynamicEditable(params),
        cellStyle: dynamicCellStyle
      };
      baseDefs.push(displayColumnDef)
    }
    return baseDefs;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasDisplayName, measureKeyMap, t]);

  const measureCdToNameMap = useMemo(() => {
    const map = {};
    
    computeMeasureData?.forEach(cm => {
      map[cm.measureCd] = cm.name;
    });
    
    return map;
  }, [computeMeasureData]);

  const rightColumnDefs = [
    {
      headerName: "userName",
      field: "userName",
      width: 100,
      hide: true,
      editable: false
    },
    {
      headerName: "measureCd",
      field: "measureCd",
      hide: true
    },
    {
      headerName: "measure",
      field: "name",
      editable: params => params.data.userName === userData.username
    },
    {
      colId: "formulaExp",
      headerName: "formula",
      field: "formulaExp",
      minWidth: 330,
      editable: params => params.data.userName === userData.username,
      headerComponent: CustomHeaderMenuIcon,
      headerComponentParams: {
        isQuestion: true,
        enableMenu: false,
        onClickedEvent: setPopoverEl,
      },
      valueGetter: params => {
        if (!params.data?.formulaExp) return "";
        
        return params.data.formulaExp.replace(/\[([^\]]+)\]/g, (match, key) => {
            const customMeasureName = measureCdToNameMap[key];
            if (customMeasureName) return `[${customMeasureName}]`;
            
            return `[${GridUtils.gridValueL10N(key)}]`;
          }
        );
      }
    },
    {
      headerName: "formula",
      field: "formulaExpValue",
      hide: true,
      suppressColumnsToolPanel: true,
    },
    {
      headerName: "computeMethod", field: "computeMethod", minWidth: 180, width: 180,
      cellEditor: "agRichSelectCellEditor", 
      cellEditorPopup: true,
      editable: false,
      valueFormatter: params => GridUtils.gridValueL10N(params.value),
      cellEditorParams: {
        values: calculationOption
      },
      filterType: 'string',
      filterOption: calculationOption,
      cellStyle: params => {
        if (!params.data.useRangeAggregation) {
          return { backgroundColor: '#f5f5f5', color: '#9e9e9e' };
        }
        return null;
      }
    },
    { 
      headerName: "useRangeAggregation", 
      field: "useRangeAggregation", 
      type: ["booleanColumn"], 
      width: 130, 
      filterType: 'boolean', 
      editable: false,
      hide: true 
    },  
    { 
      headerName: "period", 
      field: "aggregationPeriod", 
      type: ["numberColumn"], 
      width: 130, 
      filterType: 'boolean',
      editable: params =>  params.data.userName === userData.username && params.data.useRangeAggregation === true,
      cellEditor: 'agTextCellEditor',
      cellEditorParams: {
        useFormatter: true,
      },     
      valueParser: params => {
        const value = params.newValue;
        if (isNull(value) || value === '') {
          return null;
        }
        const numValue = Number(value);
        return isNaN(numValue) ? params.oldValue : Math.round(numValue);
      },
      valueFormatter: params => {
        if (isNull(params.value) || isNaN(params.value)) {
          return '';
        }
        return Math.round(params.value).toString();
      },
      cellStyle: params => {
        if (!params.data.useRangeAggregation) {
          return { backgroundColor: '#f5f5f5', color: '#9e9e9e' };
        }
        return null;
      }
    },
    {
      headerName: "descTxt",
      field: "descTxt",
      editable: params => params.data.userName === userData.username,
      minWidth: 310,
    },
    {
      headerName: "shared",
      field: "shared",
      editable: params => params.data.userName === userData.username,
      width: 90,
      type: ["booleanColumn"]
    }
  ];

  const handleClose = () => {
    if (rightGridRef.current?.api) {
      DataState.initialize(rightGridRef.current.api);
      const originalData = cloneDeep(originComputeMeasureData);
      setComputeMeasureData(originalData);
    }

    setIsExpanded(false);
    setOpen(false);
  }

  const setGridData = (selected, displayNames = null) => {
    let sortedDatas = [...allMeasure];
    
    // computeMeasures를 allMeasure에 추가
    if (!isEmpty(computeMeasureData)) {
      const computeMeasureNames = computeMeasureData.map(cm => cm.name);
      
      // 중복 제거하면서 추가
      computeMeasureNames.forEach(cm => {
        if (!sortedDatas.includes(cm)) {
          sortedDatas.push(cm);
        }
      });
    }
     
    if (!isEqual(selected, sortedDatas)) {
      const unselected = [];
      for (const measure of sortedDatas) {
        if (!selected.includes(measure)) {
          unselected.push(measure);
        }
      }
      const filteredSelected = selected.filter(select => includes(sortedDatas, select));
      sortedDatas = filteredSelected.concat(unselected);
    }

    if (!isEmpty(availableMeasures)) {
      const availableData = [];
      const unavailableData = [];
      
      for (const measure of sortedDatas) {
        if (includes(availableMeasures, measure)) {
          availableData.push(measure);  
        } else {
          unavailableData.push(measure); 
        }
      }
      
      sortedDatas = [...availableData, ...unavailableData]; 
    }

    const rowData = [];
    for (const data of sortedDatas) {
      const row = { measure: data };
      if (hasDisplayName && displayNames != null && displayNames[data] != null) {
        row.displayName = displayNames[data];
      }
      rowData.push(row);
    }
    setMeasureData(rowData);
  }

  const getMeasure = (keepOpen = false) => {
    p13nService.getMerged(viewName)
      .then(response => {
        const { data } = response;
        let measures = allMeasure;
        if (isNull(measureType)) {
          if (data?.measure) measures = data?.measure;
        } else {
          if (data?.[`measure${measureType}`]) measures = data?.[`measure${measureType}`];
        }

        if (data?.computeMeasures && !isEmpty(data.computeMeasures)) {
          setComputeMeasureData(data.computeMeasures);
          setOriginComputeMeasureData(cloneDeep(data.computeMeasures));
        } else {
          setComputeMeasureData([]);
          setOriginComputeMeasureData([]);
        }

        setGridData(measures, displayNames);
        setSelectedMeasure(measures);
        applyMeasures(measures);

      })
      .catch(e => {
        debug(e);
      }).then(() => {
        if (!keepOpen) {
          handleClose();
        }
      });
  }

  // SAVE 는 grid 노드({ selectedNodes }), RESET 은 기본 measure 목록({ selected })을 넘긴다.
  const saveMeasure = ({ selectedNodes, selected: presetSelected }) => {
    const selected = presetSelected
      ? presetSelected
      : map(sortBy(selectedNodes, "rowIndex"), "data.measure");

    const measureNm = isNull(measureType) ? "measure" : `measure${measureType}`;
    const saveData = { pageCd: viewName, configObj: { [measureNm]: selected } };

    p13nService.upsert(saveData)
      .then(() => {
        setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_SaveSuccess") });
      })
      .catch(e => {
        setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_SaveFailed") });
        debug(e);
      })
      .then(() => {
        getMeasure();
      });
  }

  const isUpdated = selected => {
    if (selected.length !== selectedMeasure.length) {
      return true;
    }

    const xorMeasure = xor(selected, selectedMeasure);
    return !isEmpty(xorMeasure);
  }

  const handleDialogOpen = (action) => {
    gridApi.stopEditing(false);

    const selectedNodes = gridApi.getSelectedNodes();
    if (action === DIALOG_TITLE.SAVE) {
      // 저장 전 필수 지표 검증 (그리드에 존재하는 필수 지표만 대상)
      const gridMeasures = [];
      gridApi.forEachNode(n => gridMeasures.push(n.data.measure));
      const selectedMeasures = selectedNodes.map(n => n.data.measure);
      const missingRequired = requiredMeasure.filter(m => gridMeasures.includes(m) && !selectedMeasures.includes(m));
      if (!isEmpty(missingRequired)) {
        setSnackInfo({ open: true, severity: snackBarMessageColor.noMatchCondition, content: t("MSG_RequiredMeasure", { measure: t(missingRequired[0]) }) });
        return;
      }

      if (selectedNodes.length > 0) {
        setDialogInfo({ open: true, title: "save", content: t("MSG_SaveConfirm"), el: { selectedNodes } });
      } else {
        setSnackInfo({ open: true, severity: snackBarMessageColor.noMatchCondition, content: t("MSG_SelectLeastOneMeasure") });
      }

    } else if (action === DIALOG_TITLE.RESET) {
      const allDefaultMeasure = isEmpty(defaultMeasure) ? allMeasure : defaultMeasure;
      setDialogInfo({ open: true, title: "reset", severity: "info", content: t("MSG_ResetMeasure"), el: { selected: allDefaultMeasure } });
    } else if (action === DIALOG_TITLE.CLOSE) {
      if (isUpdated(selectedNodes)) {
        setDialogInfo({ open: true, title: "close", content: t("MSG_ConfirmToClose") });
      } else {
        handleClose();
      }
    }
  }

  const handleDialog = (action, _falseAction, el) => {
    switch (action) {
      case DIALOG_TITLE.SAVE:
      case DIALOG_TITLE.RESET: {
        saveMeasure(el);
        break;
      }
      case DIALOG_TITLE.CLOSE: {
        handleClose();
        break;    
      }      
      case DIALOG_TITLE.DELETE: {
        handleDeleteCustomMeasure();
        break;
      }
      default: break;
    }
  }  
  
  // Custom Measure 의존성 체크
  const checkCustomMeasureDependencies = (measureNamesToDelete) => {
    const dependencies = [];
    const addedDependencies = new Set(); 

    rightGridRef.current?.api?.forEachNode(node => {
      const { name, formulaExp } = node.data;
      
      if (!measureNamesToDelete.includes(name) && formulaExp) {
        // formulaExp에서 삭제 대상 measure가 참조되는지 확인
        measureNamesToDelete.forEach(deleteName => {
          if (!addedDependencies.has(deleteName)) {
            // [deleteName]
            const escapedName = deleteName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
            const regex = new RegExp(`\\[${escapedName}\\]`);
            
            // formulaExp에 [deleteName]이 있는지 체크
            if (regex.test(formulaExp)) {
              addedDependencies.add(deleteName);
              dependencies.push({
                dependentMeasure: name,     
                deletingMeasure: deleteName 
              });
            }
          }
        });
      }
    });
    
    return dependencies;
  };

  // Custom Measure Button Handlers
  const handleDeleteCustomMeasure = () => {
    rightGridRef.current?.api?.stopEditing(true);

    const selectedNodes = rightGridRef.current?.api?.getSelectedNodes();
    const selectedData = selectedNodes.map(node => node.data);
    const deletedNames = selectedData.map(item => item.name);

    // 의존성 체크
    const dependencies = checkCustomMeasureDependencies(deletedNames);
    
    if (!isEmpty(dependencies)) {
      const deletingMeasures = dependencies.map(dep => 
        ` "${dep.deletingMeasure}"`
      );
      
      setSnackInfo({ 
        open: true, 
        severity: "warning", 
        content: t("MSG_MEASURE_DEPENDENCY_WARNING", { measures: deletingMeasures })
      });

      return;
    }

    deleteCustomMeasure(selectedData, deletedNames);
  };

  const deleteCustomMeasure = (selectedData, deletedNames) => {
    rightGridRef.current?.api?.applyTransaction({ remove: selectedData });

    const allComputeMeasures = [];
    const allSharedComputeMeasures = [];
    
    originComputeMeasureData.forEach(item => {
      if (item.userName !== userData.username) {
        return;
      }
      
      if (deletedNames.includes(item.name)) {
        return;
      }
      
      if (item.shared) {
        allSharedComputeMeasures.push(item);
      } else {
        allComputeMeasures.push(item);
      }
    });

    // 왼쪽 grid의 선택된 measure 가져오기
    const leftSelectedNodes = gridApi.getSelectedNodes();
    const sortedNodes = sortBy(leftSelectedNodes, "rowIndex");
    let selectedMeasures = map(sortedNodes, "data.measure");

    // 삭제된 compute measure를 selectedMeasures에서 제거
    selectedMeasures = selectedMeasures.filter(measure => 
      !deletedNames.includes(measure)
    );

    const measureNm = isNull(measureType) ? "measure" : `measure${measureType}`;

    const saveData = { 
      pageCd: viewName, 
      configObj: { 
        [measureNm]: selectedMeasures,
        computeMeasures: allComputeMeasures
      } 
    };

    p13nService.upsert(saveData)
      .then(() => {
        setSnackInfo({ 
          open: true, 
          severity: snackBarMessageColor.saveSuccess, 
          content: t("MSG_DeleteSuccess") 
        });
      })
      .catch(e => {
        setSnackInfo({ 
          open: true, 
          severity: snackBarMessageColor.saveFail, 
          content: t("MSG_DeleteFailed") 
        });
        debug(e);
      })
      .then(() => {
        const sharedSaveData = {
          pageCd: `${viewName}${common}`,
          configObj: {
            computeMeasures: allSharedComputeMeasures 
          }
        };

        p13nService.upsert(sharedSaveData)
          .then(() => {
          })
          .catch(e => {
            setSnackInfo({ 
              open: true, 
              severity: snackBarMessageColor.saveFail, 
              content: t("MSG_SaveFailed") 
            });
            debug(e);
          })
          .finally(() => {
            getMeasure(true);
          });
      });
  };

  const handleAddCustomMeasure = (computeMethod) => {
   const newRow = computeMethod ? 
    {
      userName: userData.username,
      measureCd: "",
      name: "",
      formulaExp: "",
      computeMethod,
      useRangeAggregation: true,
      aggregationPeriod: 1,
      descTxt: "",
      activeFlg: true,
      shared: false
    }
    : 
    {
      userName: userData.username,
      measureCd: "",
      name: "",
      formulaExp: "",
      useRangeAggregation: false,
      aggregationPeriod: null,
      descTxt: "",
      activeFlg: true,
      shared: false
    }

    DataState.setDataState(GridUtils.addRow(rightGridRef.current.api, newRow));
  };
  
  const handleRemoveCustomMeasure = () => {
    // 선택된 row 가져오기
    const selectedNodes = rightGridRef.current?.api?.getSelectedNodes();
    
    if (!selectedNodes || selectedNodes.length === 0) {
      setSnackInfo({ 
        open: true, 
        severity: snackBarMessageColor.noMatchCondition, 
        content: t("MSG_DeleteNoData") 
      });
      return;
    }
    
    // 선택된 row가 있으면 삭제 확인 dialog 표시
    setDialogInfo({ open: true, title: "delete", content: t("MSG_DeleteConfirm") });
  };
  
  const handleSaveCustomMeasure = () => {
    const dataState = DataState.getAllStateData(rightGridRef.current?.api);
    
    if (isEmpty(dataState)) { // 변경사항 없음 체크
      setSnackInfo({
        open: true,
        severity: snackBarMessageColor.noMatchCondition,
        content: t("MSG_NoSaveData")
      });
      return;
    }    
    
    const allComputeMeasures = [];
    const allSharedComputeMeasures = [];
    
    // 기존 measureCd → name 매핑 (이름 변경 추적용)
    const oldMeasureCdToNameMap = {};

    originComputeMeasureData.forEach(cm => {
      oldMeasureCdToNameMap[cm.measureCd] = cm.name;
    });    
    
    // 기존 measureCd 보존하면서 데이터 수집 및 name 변경 감지
    let computeMeasures = [];
    const nameChanges = {}; // oldName → newName 매핑
    const computeMeasureNameToCdMap = {};
    
    rightGridRef.current?.api?.forEachNode(node => {
      const { measureCd, name } = node.data;
      
      computeMeasures.push(name);
      
      if (measureCd) {
        computeMeasureNameToCdMap[name] = measureCd;
        
        // 이름 변경 감지 (기존 measureCd의 이전 name과 비교)
        const oldName = oldMeasureCdToNameMap[measureCd];
        if (oldName && oldName !== name) {
          nameChanges[oldName] = name;
        }
      }
    });

    // insert data 에 measure cd 반영 
    let newIndex = 0;
    rightGridRef.current?.api?.forEachNode(node => {
      const { measureCd, name, useRangeAggregation } = node.data;
      
      if (!measureCd) {
        const baseCd = getSafeBaseCd(userData, name);
        let newMeasureCd;
        
        if (useRangeAggregation) {
          newMeasureCd = `R_${baseCd}_${newIndex}`;
          newIndex++;
        } else {
          newMeasureCd = `C_${baseCd}_${newIndex}`;
          newIndex++;
        }
        
        computeMeasureNameToCdMap[name] = newMeasureCd;
      }
    });

    // 언어팩 measure → measure key 매핑 (formulaExp 변환에서 사용)
    const translationMap = {};

    measureData.forEach(item => {
      const original = item.measure;
      const measureKey = measureKeyMap[original] || original;    
      const translated = computeMeasures.indexOf(measureKey) < 0 ? GridUtils.gridValueL10N(measureKey) : measureKey;
      translationMap[translated] = measureKey;
    });

    // allComputeMeasures = final save data
    rightGridRef.current?.api?.forEachNode(node => {
      const { userName, name, formulaExp, computeMethod, useRangeAggregation, aggregationPeriod, descTxt, activeFlg, shared } = node.data;
 
      if (userData.username !== userName) {
        return; // 본인이 생성한 measure만 저장 대상으로 처리
      }

      const measureCd = computeMeasureNameToCdMap[name];

      // formulaExp 에 반영시는 [name] -> [measureCd] 

      const originalFormulaValue = (formulaExp || "").replace(
        /\[([^\]]+)\]/g,
        (match, translatedName) => {
          const originalName = translationMap[translatedName] || translatedName;
          return `[${originalName}]`;
        }
      );
      
      // 최종적으로 [name]을 [UPPER_SNAKE_CASE]로 변환
      const convertedFormulaExpValue = originalFormulaValue.replace(
        /\[([^\]]+)\]/g,
        (match, measureName) => computeMeasures.indexOf(measureName) < 0 ? `[${toUpperSnakeCase(measureName)}]` : `[${computeMeasureNameToCdMap[measureName]}]`
      );

      const measureData = {
        measureCd: measureCd, 
        name: name, 
        formulaExp, 
        formulaExpValue: convertedFormulaExpValue,
        computeMethod,
        useRangeAggregation,
        aggregationPeriod,
        descTxt, 
        activeFlg,
        shared
      };

      if (shared) {
        allSharedComputeMeasures.push(measureData);
      } else {
        allComputeMeasures.push(measureData);
      }

    });

    const allMeasuresForValidation = [
      ...allComputeMeasures,
      ...allSharedComputeMeasures
    ];

    // 검증 1: 빈 name
    const emptyNamesResult = validateEmptyNames(allMeasuresForValidation, t);

    if (!emptyNamesResult.isValid) {
      setSnackInfo({ open: true, severity: snackBarMessageColor.noMatchCondition, content: emptyNamesResult.message });
      return;
    }

    // 검증 2: name 중복
    const duplicateResult = validateDuplicateNames(rightGridRef, t);

    if (!duplicateResult.isValid) {
      setSnackInfo({ open: true, severity: snackBarMessageColor.noMatchCondition, content: duplicateResult.message });
      return;
    }   
      // 검증 3: aggregation Period 값 유효성
    const periodResult = validateAggregationPeriod(allMeasuresForValidation, t);
    
    if (!periodResult.isValid) {
      setSnackInfo({ open: true, severity: snackBarMessageColor.noMatchCondition, content: periodResult.message });
      return;
    }
    
    // 이름 변경 처리 (formulaExp 내 참조 업데이트)
    processNameChanges(nameChanges, allComputeMeasures, allSharedComputeMeasures,  computeMeasureNameToCdMap, translationMap, computeMeasures);

    // 검증 4: 참조 유효성 (존재하지 않는 참조 + 순환 참조 통합)
    const referencesResult = validateReferences(allComputeMeasures, allSharedComputeMeasures, measureData, measureKeyMap, t);

    if (!referencesResult.isValid) {
      setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: referencesResult.message });
      return;
    }

    // 왼쪽 grid의 선택된 measure 가져오기
    const leftSelectedNodes = gridApi.getSelectedNodes();
    const sortedNodes = sortBy(leftSelectedNodes, "rowIndex");
    let selectedMeasures = map(sortedNodes, "data.measure");

    selectedMeasures = selectedMeasures.map(measure => {
      return nameChanges[measure] || measure;
    });

    const measureNm = isNull(measureType) ? "measure" : `measure${measureType}`;
    const saveData = { 
      pageCd: viewName, 
      configObj: { 
        [measureNm]: selectedMeasures,
        computeMeasures: allComputeMeasures
      } 
    };

    p13nService.upsert(saveData)
      .then(() => {
        setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_SaveSuccess") });
        DataState.initialize(rightGridRef.current?.api);
      })
      .catch(e => {
        setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_SaveFailed") });
        debug(e);
      })
      .then(() => {
        const saveData = {
          pageCd: `${viewName}${common}`,
          configObj: {
            computeMeasures: allSharedComputeMeasures 
          }
        };

        p13nService.upsert(saveData)
          .then(() => {
            setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_SaveSuccess") });
            DataState.initialize(rightGridRef.current?.api);
          })
          .catch(e => {
            setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_SaveFailed") });
            debug(e);
          })
          .then(() => {
            getMeasure(true);
          });
      });
  };

  const onRightGridReady = () => {
    setGridLoading(true);
  }

  const onRowSelected = params => {
    const { node, data } = params;
    if (node.isSelected() || !requiredMeasure.includes(data.measure)) return;
    // rowData 교체(초기화/저장 후 재조회) 시 AG Grid 는 기존 노드를 해제(rowSelected)한 직후 같은 호출 안에서 destroy 한다.
    // 판단을 마이크로태스크로 미뤄 destroy 된 노드(데이터 교체)는 무시하고, 살아 있는 노드의 해제(체크박스·Shift 범위 선택 등)만 막는다.
    // (source 로는 구분 불가: 전체 선택 후 Shift 범위 선택의 reset 도 source "api" 로 해제 이벤트를 보낸다)
    queueMicrotask(() => {
      if (node.destroyed || node.isSelected()) return;
      setSnackInfo({ open: true, severity: snackBarMessageColor.noMatchCondition, content: t("MSG_RequiredMeasure", { measure: t(data.measure) }) });
      node.setSelected(true);
    });
  }

  const selectRow = () => {
    gridApi?.forEachNode(node => {
      if (selectedMeasure.includes(node.data.measure)) {
        node.setSelected(true);
      }
    });
  }

  useEffect(() => {
    if (!gridApi?.destroyCalled && !isEmpty(measureData) && !isEmpty(selectedMeasure)) {
      selectRow();
    }

    if (gridLoading) {
      DataState.initialize(rightGridRef.current?.api);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gridApi, measureData, selectedMeasure, gridLoading]);

  useEffect(() => {
    if (measureType) {
      getMeasure();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [measureType])

  useEffect(() => {
    if (!isEmpty(allMeasure)) {
      getMeasure();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allMeasure]);

  const gridHeight = Math.min(500, (allMeasure?.length || 0) * 30);
  const gridWidth = hasDisplayName ? 500 : 320;

  // 우측 커스텀 측정치 패널을 펼치면 폭이 늘어난다 — 프리셋으로는 표현이 안 돼 width 를 직접 준다
  const modalWidth = isExpanded ? 1272 : hasDisplayName ? 532 : 352;

  const onCellKeyDown = e => {
    if (isEqual(e.column.colId, "formulaExp") && isEqual(e.event.key, "[")) {
      const path = [];
      const getParent = (list, element) => {
        if (element?.parentElement) {
          list.push(element?.parentElement);
          return getParent(list, element?.parentElement);
        }
        return null;
      };
      getParent(path, e.event.target);
      setMenuEl(path[3]);
      
      editingNodeRef.current = e.node;
    }
  };

  const menuClose = () => {
    setMenuEl(null);
  };
  
  const addExpression = (measureValue) => {
    const api = rightGridRef.current?.api;
    const targetNode = editingNodeRef.current;
    
    if (!targetNode) {
      menuClose();
      return;
    }   

    const measureKey = measureKeyMap[measureValue] || measureValue;
    const translatedMeasure = GridUtils.gridValueL10N(measureKey);
    const currentFormulaDisplay = targetNode?.data?.formulaExp || "";
    const newDisplayValue = `${currentFormulaDisplay}${translatedMeasure}]`;

    //  measureTranslationMap 기반으로 [] 안의 단어 변환
    const convertedDisplayValue = newDisplayValue.replace(/\[([^\]]+)\]/g, (match, word) => {
        return measureTranslationMap[word] ? `[${measureTranslationMap[word]}]` : match;
      }
    );

    api?.stopEditing();

    targetNode.setDataValue("formulaExp", convertedDisplayValue);

    api?.startEditingCell({
      rowIndex: targetNode.rowIndex,
      colKey: "formulaExp"
    });
    
    editingNodeRef.current = null;

    menuClose();
  };

  const addMenuHandleClick = (event) => {
    setAnchorEl(event.currentTarget);
  }

  const addListClose = () => {
    setAnchorEl(null);
  };

  const handleCellValueChanged = (params) => {
    if (params.column.colId !== 'formulaExp') {
      DataState.setDataState(params);
      return;
    }

    const { newValue, oldValue } = params;
    
    // 변경 없으면 스킵
    if (newValue === oldValue) return;
    
    // 빈 값이면 그대로 저장
    if (!newValue) {
      DataState.setDataState(params);
      return;
    }
    
    const convertedValue = newValue.replace(/\[([^\]]+)\]/g, (match, translated) => {
        return measureTranslationMap[translated] ? `[${measureTranslationMap[translated]}]` : match;
      }
    );
    
    // 변환이 발생했으면 업데이트
    if (convertedValue !== newValue) {
      params.node.setDataValue('formulaExp', convertedValue);
    }
    
    DataState.setDataState(params);
  };

  return <>
    {/* 저장 버튼은 p13nService.upsert 를 한다 — 저장이므로 셸 기본값(save + SaveIcon) 그대로. */}
    <BaseConfigModal
      open={open}
      onClose={() => handleDialogOpen(DIALOG_TITLE.CLOSE)}
      title={t(MODAL_TITLE)}
      titleAdornment={enableCustomMeasure ? (
        <IconButton
          size="small"
          onClick={() => setIsExpanded(!isExpanded)}
          sx={{
            ml: 1,
            color: isExpanded ? 'orangered' : 'cornflowerblue'
          }}
          aria-label="toggle-expand"
        >
          {isExpanded ? <KeyboardArrowLeftIcon /> : <KeyboardArrowRightIcon />}
        </IconButton>
      ) : null}
      onSave={() => handleDialogOpen(DIALOG_TITLE.SAVE)}
      extraActions={
        <Button size="small"
          startIcon={<ResetIcon />}
          onClick={() => handleDialogOpen(DIALOG_TITLE.RESET)}>
          {t("reset")}
        </Button>
      }
      width={modalWidth}
      paperSx={{ transition: 'width 0.3s ease-in-out' }}
    >
          <Box sx={{ display: 'flex', gap: 2 }}>
          {/* Left Panel - Measure Grid */}
          <Box sx={{ width: gridWidth }}>
            <Box className="ag-theme-balham" sx={{ my: 1, height: gridHeight, width: gridWidth, minHeight: 250 }}>
              <AgGridReact
                rowData={measureData}
                columnDefs={GridUtils.getColumnDefs(columnDefs)}
                {...DefaultGridSetting({ title: MODAL_TITLE })}
                rowDragManaged
                rowSelection={{ mode: 'multiRow', enableClickSelection: false, checkboxes: true, headerCheckbox: true, selectAll: 'filtered' }}
                animateRows
                onRowSelected={onRowSelected}
                onGridReady={params => setGridApi(params.api)}
              />
            </Box>
          </Box>
          {/* Right Panel - Additional Content (Expanded) */}
          {isExpanded && (
            <Box sx={{ 
              flex: 1, 
              minWidth: 500,
              height: gridHeight,
              minHeight: 250,
              borderLeft: '1px solid',
              borderColor: 'divider',
              pl: 2,
              display: 'flex',
              flexDirection: 'column',
              my: 1
            }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="subtitle1">
                  {t("customMeasureSettings")}
                </Typography>
                <Box>
                  <AddButton 
                    id="add-button"
                    aria-controls={addListOpen ? "add-list-menu" : null}
                    aria-haspopup="true"
                    aria-expanded={addListOpen ? "true" : "false"}
                    onClick={addMenuHandleClick}
                   />
                  <RemoveButton onClick={() => handleRemoveCustomMeasure()} />
                  <SaveButton onClick={() => handleSaveCustomMeasure()} />
                </Box>
              </Box>              
              <Box className="ag-theme-balham" sx={{
                height: gridHeight,
                minHeight: 250,
                width: '100%'
              }}>
                <AgGridReact
                  ref={rightGridRef}
                  rowData={computeMeasureData}
                  columnDefs={GridUtils.getColumnDefs(rightColumnDefs)}
                  {...DefaultGridSetting({})}
                  rowSelection={{
                    mode: 'multiRow', 
                    enableClickSelection: false,
                    checkboxes: params => params.data.userName === userData.username,
                    headerCheckbox: true,
                    selectAll: 'filtered'
                  }}
                  onGridReady={onRightGridReady}
                  onCellKeyDown={onCellKeyDown}
                  onCellValueChanged={handleCellValueChanged}
                />
              </Box>
            </Box>
          )}
        </Box>
    </BaseConfigModal>
    <Snackbar
      open={snackInfo.open}
      onClose={() => setSnackInfo({ ...snackInfo, open: false })}
      severity={snackInfo.severity}
      content={snackInfo.content}
    />
    <Popover
      id={popoverId}
      open={popoverOpen}
      anchorEl={popoverEl}
      onClose={popoverClose}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "left",
      }}>
      <Typography sx={{ p: 1.2, fontSize: FS.H2 }}>
        {t("MSG_EditableFormulaExplanation")}
        <br></br>
        {t("availableCals")}:<strong> + - * / </strong>
        <br></br>
        {t("MSG_FormulaExplanation")} <br></br>
        ex&#41; &#91;MEASURE&#93;* 1.1
      </Typography>
    </Popover>
    <Menu
      id="long-menu"
      MenuListProps={{
        "aria-labelledby": "outlined-basic",
      }}
      anchorEl={menuEl}
      open={menuOpen}
      onClose={menuClose}
      PaperProps={{
        style: {
          maxHeight: 200,
          width: "25ch",
        },
      }}>      
      {measureData?.map(computeMeasure => {
        const measureKey = measureKeyMap[computeMeasure.measure] || computeMeasure.measure;
        return (
          <MenuItem
            onClick={() => addExpression(computeMeasure.measure)}
            value={computeMeasure.measure}
            key={computeMeasure.measure}>
            {GridUtils.gridValueL10N(measureKey)}
          </MenuItem>
        );
      })}
    </Menu>
    <Dialog
      open={dialogInfo.open}
      onClose={() => setDialogInfo({ open: false, title: "", content: "" })}
      title={dialogInfo.title}
      content={dialogInfo.content}
      onHandler={handleDialog}
      el={dialogInfo.el}
    />
    <Menu
      id="add-list-menu"
      anchorEl={anchorEl}
      open={addListOpen}
      onClose={addListClose}
      aria-labelledby="add-button"
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "right",
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "right",
      }}
    >
      <MenuItem sx={{ fontSize: FS.TITLE, p: "3px 5px" }} onClick={() => handleAddCustomMeasure()}>{t("addMeasure")}</MenuItem>
      <MenuItem sx={{ fontSize: FS.TITLE, p: "3px 5px" }} onClick={() => handleAddCustomMeasure("sum")}>{t("addSumMeasure")}</MenuItem>
      <MenuItem sx={{ fontSize: FS.TITLE, p: "3px 5px" }} onClick={() => handleAddCustomMeasure("avg")}>{t("addAvgMeasure")}</MenuItem>
    </Menu>
  </>
}

export default withTranslation()(MeasureModal);